import axios from 'axios'
import { getCsrfToken } from './utils'

/**
 * Determine the SRO API base URL based on the current domain.
 * SRO routes live at /sro/api/ (not under /api/), so we use a separate client.
 */
function getSroBaseUrl(): string {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL.replace(/\/api$/, '') + '/sro/api'
  }

  const hostname = window.location.hostname
  const protocol = window.location.protocol

  if (hostname === 'app.devfctrz.com' || hostname.endsWith('.devfctrz.com')) {
    return `${protocol}//api.devfctrz.com/sro/api`
  }

  if (hostname === 'app.chatterpillar.io' || hostname.endsWith('.chatterpillar.io')) {
    return `${protocol}//api.chatterpillar.io/sro/api`
  }

  return '/sro/api'
}

const sroClient = axios.create({
  baseURL: getSroBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true,
})

sroClient.interceptors.request.use((config) => {
  const csrfToken = getCsrfToken()
  if (csrfToken) {
    config.headers['X-CSRFToken'] = csrfToken
  }
  try {
    const stored = localStorage.getItem('tenant')
    if (stored) {
      const tenant = JSON.parse(stored)
      if (tenant.subdomain) {
        config.headers['X-Tenant-Subdomain'] = tenant.subdomain
      }
    }
  } catch {}
  return config
})

sroClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      window.location.href = '/accounts/login/'
    }
    return Promise.reject(error)
  }
)

export const sroApi = {
  // Config list / create
  getConfigs: () => sroClient.get('/configs/'),
  createConfig: (data: { name: string }) => sroClient.post('/configs/', data),

  // Config detail / update / delete
  getConfig: (configId: number) => sroClient.get(`/configs/${configId}/`),
  updateConfig: (configId: number, data: any) => sroClient.put(`/configs/${configId}/`, data),
  deleteConfig: (configId: number) => sroClient.delete(`/configs/${configId}/`),

  // Export / Import
  exportConfig: (configId: number) => sroClient.get(`/configs/${configId}/export/`),
  importConfig: (data: any) => sroClient.post('/configs/import/', data),

  // Intents (scoped to config)
  getIntents: (configId: number) => sroClient.get(`/configs/${configId}/intents/`),
  createIntent: (configId: number, data: any) => sroClient.post(`/configs/${configId}/intents/`, data),
  updateIntent: (configId: number, id: number, data: any) => sroClient.put(`/configs/${configId}/intents/${id}/`, data),
  deleteIntent: (configId: number, id: number) => sroClient.delete(`/configs/${configId}/intents/${id}/`),

  // Departments (scoped to config)
  getDepartments: (configId: number) => sroClient.get(`/configs/${configId}/departments/`),
  createDepartment: (configId: number, data: any) => sroClient.post(`/configs/${configId}/departments/`, data),
  updateDepartment: (configId: number, id: number, data: any) => sroClient.put(`/configs/${configId}/departments/${id}/`, data),
  deleteDepartment: (configId: number, id: number) => sroClient.delete(`/configs/${configId}/departments/${id}/`),

  // Sub-departments (scoped to config)
  getSubDepartments: (configId: number, deptId: number) => sroClient.get(`/configs/${configId}/departments/${deptId}/sub-departments/`),
  createSubDepartment: (configId: number, deptId: number, data: any) => sroClient.post(`/configs/${configId}/departments/${deptId}/sub-departments/`, data),
  updateSubDepartment: (configId: number, deptId: number, subId: number, data: any) => sroClient.put(`/configs/${configId}/departments/${deptId}/sub-departments/${subId}/`, data),
  deleteSubDepartment: (configId: number, deptId: number, subId: number) => sroClient.delete(`/configs/${configId}/departments/${deptId}/sub-departments/${subId}/`),

  // Service Area (scoped to config)
  getServiceArea: (configId: number) => sroClient.get(`/configs/${configId}/service-area/`),
  updateServiceArea: (configId: number, data: any) => sroClient.put(`/configs/${configId}/service-area/`, data),

  // Sessions
  getSessions: () => sroClient.get('/sessions/'),
  getSession: (sessionId: string) => sroClient.get(`/sessions/${sessionId}/`),
  deleteSession: (sessionId: string) => sroClient.delete(`/sessions/${sessionId}/delete/`),
  getSessionStats: () => sroClient.get('/sessions/stats/'),

  // Chat (public)
  sendMessage: (message: string, sessionId?: string) => sroClient.post('/chat/', { message, session_id: sessionId }),

  // Health
  healthCheck: () => sroClient.get('/health/'),
}

export default sroApi
